import { TableBody, TableCell, TableRow, useMediaQuery, useTheme } from '@mui/material'
import ProjectTitleTableCell from './ProjectTitleTableCell'
import { StickyTableLeftCell } from '../../styles/TableStyles'
import EditableParticipationTableCell from './EditableParticipationTableCell'
import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { Project, User, WorkforceSchedule } from '../../store/types'
import Demand from '../../store/types/models/Demand'
import { useGetRemainingWorkforceSchedulesQuery } from '../../store/redux/reducers/workforceScheduleSlice'
import { Dayjs } from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import { t } from 'i18next'

const MonthDistributionTableBody = ({
  isEditionDisabled,
  monthData,
  dbDate,
  leaderId,
  date,
  stateProjects,
  handleProjectRemove,
  sortedEmployees,
  secondColumnLeftSpace,
  demandResponseContent,
  isEditMode,
  firstProjectRef,
  vacancyRef,
}: {
  isEditionDisabled(): boolean
  monthData: WorkforceSchedule[]
  dbDate: string
  leaderId: string | undefined
  date: Dayjs
  stateProjects: Project[]
  handleProjectRemove(project: Project): void
  sortedEmployees: User[]
  secondColumnLeftSpace: number
  demandResponseContent: Demand[]
  isEditMode: boolean
  firstProjectRef: RefObject<HTMLDivElement>
  vacancyRef: React.RefObject<HTMLButtonElement>
}) => {
  const employeesIds = sortedEmployees?.map(employee => employee.id)
  const { data: remainingWorkforce } = useGetRemainingWorkforceSchedulesQuery(
    {
      id: leaderId!!,
      props: {
        date: date.format(DateFormat.YearMonth),
        excludedProjects: stateProjects?.map(project => project.id),
        employeesList: employeesIds,
      },
    },
    { skip: employeesIds.length === 0 }
  )
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const sortedProjects = useMemo(() => {
    return [...stateProjects].sort((a, b) => a.name.localeCompare(b.name))
  }, [stateProjects])

  const calculateRemaining = (user: User, remaining: Map<string, number>, total: number): number => {
    return (remaining.get(user.id) ?? user.employmentPercentage ?? 100) - total
  }

  const calculateEmployeeTotal = (data: WorkforceSchedule[], employeeId: string): number => {
    return data
      .filter(workforce => workforce.employee.id === employeeId)
      .reduce((total, currentValue) => {
        return total + currentValue.percent
      }, 0)
  }

  const calculateProjectTotal = (data: WorkforceSchedule[], projectId: string, demandResponse: Demand[]): number => {
    let total = data
      .filter(workforce => workforce.project.id === projectId)
      .reduce((total, currentValue) => {
        return total + currentValue.percent
      }, 0)

    const demand = demandResponse.find(d => d.project.id === projectId)
    if (demand) {
      total += demand.numberOfPeople * 100
    }
    return total
  }

  const calculateTotal = (data: WorkforceSchedule[], demandResponse: Demand[]): number => {
    let total = data.reduce((total, currentValue) => total + currentValue.percent, 0)

    total += demandResponse.reduce((total, demand) => total + demand.numberOfPeople * 100, 0)

    return total
  }

  const [totalRemaining, setTotalRemaining] = useState<number>(0)

  const calculateTotalRemaining = useCallback((): number => {
    const remaining: Map<string, number> = new Map(Object.entries(remainingWorkforce ?? {}))
    return sortedEmployees.reduce((acc, user) => {
      const userRemaining = remaining.get(user.id) ?? user.employmentPercentage ?? 100
      const userWorkforceTotal = calculateEmployeeTotal(monthData, user.id)

      return acc + (userRemaining - userWorkforceTotal)
    }, 0)
  }, [sortedEmployees, remainingWorkforce, monthData])

  useEffect(() => {
    setTotalRemaining(calculateTotalRemaining())
  }, [calculateTotalRemaining])

  return (
    <TableBody>
      {sortedProjects.map((project, index) => {
        return (
          <TableRow key={index} sx={{ height: '50px' }}>
            <ProjectTitleTableCell
              project={project}
              onRemoveIconClick={() => handleProjectRemove(project)}
              isEditionDisabled={isEditionDisabled()}
              index={index}
              firstProjectRef={firstProjectRef}
              vacancyRef={vacancyRef}
            />
            <StickyTableLeftCell
              sx={{
                borderBottom: '1px solid',
                borderColor: 'background.elevation8',
                left: secondColumnLeftSpace,
                fontWeight:
                  calculateProjectTotal(monthData, project.id, demandResponseContent) === 0 ? 'normal' : 'bold',
                position: isDownSm ? 'unset' : 'sticky',
              }}
            >
              {calculateProjectTotal(monthData, project.id, demandResponseContent) === 0
                ? '-'
                : `${calculateProjectTotal(monthData, project.id, demandResponseContent)}%`}
            </StickyTableLeftCell>
            {sortedEmployees.map((employee, index) => {
              const workforceData = monthData.find(
                workforce =>
                  workforce.employee.id === employee.id &&
                  workforce.project.id === project.id &&
                  workforce.date === dbDate
              )

              return (
                <EditableParticipationTableCell
                  key={index}
                  isEditMode={isEditMode}
                  workforceData={workforceData}
                  currentEmployee={employee}
                  project={project}
                  date={date}
                  isEditionDisabled={isEditionDisabled()}
                />
              )
            })}
            <StickyTableLeftCell
              padding="checkbox"
              sx={{
                right: 0,
                borderBottom: '1px solid',
                borderColor: 'background.elevation8',
              }}
            ></StickyTableLeftCell>
          </TableRow>
        )
      })}
      {sortedEmployees.length > 0 && sortedProjects.length > 0 && (
        <TableRow sx={{ height: '50px' }}>
          <StickyTableLeftCell
            sx={{
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'background.elevation8',
              color: 'text.highEmphasis',
              fontWeight: 700,
            }}
          >
            {t('page.projectDistribution.left')}
          </StickyTableLeftCell>
          <StickyTableLeftCell
            sx={{
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'background.elevation8',
              left: secondColumnLeftSpace,
              fontWeight: totalRemaining === 0 ? 'normal' : 'bold',
              color: totalRemaining >= 0 ? 'text.highEmphasis' : 'error.main',
              position: isDownSm ? 'unset' : 'sticky',
            }}
          >
            {`${totalRemaining}%`}
          </StickyTableLeftCell>
          {sortedEmployees.map((employee, index) => {
            const employeeTotalSum = calculateRemaining(
              employee,
              new Map(Object.entries(remainingWorkforce || {})),
              calculateEmployeeTotal(monthData, employee.id)
            )
            return (
              <TableCell
                sx={{
                  borderTop: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'background.elevation8',
                  fontWeight: employeeTotalSum >= 0 ? 'normal' : 'bold',
                  color: employeeTotalSum >= 0 ? 'text.highEmphasis' : 'error.main',
                }}
                align="center"
                key={index}
              >
                {`${employeeTotalSum}%`}
              </TableCell>
            )
          })}
          <StickyTableLeftCell
            padding="checkbox"
            sx={{
              right: 0,
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'background.elevation8',
            }}
          ></StickyTableLeftCell>
        </TableRow>
      )}
      <TableRow sx={{ height: '50px' }}>
        <StickyTableLeftCell
          sx={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'background.elevation8',
            color: 'text.highEmphasis',
            fontWeight: 700,
          }}
        >
          {t('page.projectDistribution.sum')}
        </StickyTableLeftCell>
        <StickyTableLeftCell
          sx={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'background.elevation8',
            left: secondColumnLeftSpace,
            fontWeight: calculateTotal(monthData, demandResponseContent) === 0 ? 'normal' : 'bold',
            position: isDownSm ? 'unset' : 'sticky',
          }}
        >
          {calculateTotal(monthData, demandResponseContent) === 0
            ? '-'
            : `${calculateTotal(monthData, demandResponseContent)}%`}
        </StickyTableLeftCell>
        {sortedEmployees.map((employee, index) => {
          const employeeTotalSum = calculateEmployeeTotal(monthData, employee.id)
          return (
            <TableCell
              sx={{
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'background.elevation8',
                fontWeight: employeeTotalSum === 0 ? 'normal' : 'bold',
              }}
              align="center"
              key={index}
            >
              {employeeTotalSum === 0 ? '-' : `${employeeTotalSum}%`}
            </TableCell>
          )
        })}
        <StickyTableLeftCell
          padding="checkbox"
          sx={{
            right: 0,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'background.elevation8',
          }}
        ></StickyTableLeftCell>
      </TableRow>
    </TableBody>
  )
}

export default MonthDistributionTableBody